// ─────────────────────────────────────────────────────────────────────────
// Activos Fijos (27-ago-2026). Parque de bienes del taller: tornos,
// fresadoras, camioneta, computadores… Tarjetas por activo con su
// depreciación lineal calculada por el backend (services/depreciacion.js):
// acumulada, valor libro, meses restantes. Alta/edición con vida útil
// pre-llenada según la sugerencia SII de la categoría (editable), y baja o
// venta con confirmación — nunca se borra: la historia se conserva.
// Se monta como pestaña "Activos" dentro de Contabilidad.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from "react";
import backend from "../../backend";
import useIsMobile from "../../hooks/useIsMobile";
import { formatPrice } from "../../utils";

const fmtM = (v) => (v != null ? `$${formatPrice(Math.round(v))}` : "—");
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const num = { fontVariantNumeric: "tabular-nums" };

// Espejo de CATEGORIAS + VIDA_UTIL_SUGERIDA del backend
// (backend/models/activoFijo.js) — meses según tabla SII Res. Ex. N°43/2002.
const CATEGORIAS = [
  { key: "maquinaria",   label: "Maquinaria",    emoji: "🏭", vida: 180 },
  { key: "vehiculos",    label: "Vehículos",     emoji: "🚚", vida: 84 },
  { key: "herramientas", label: "Herramientas",  emoji: "🔧", vida: 36 },
  { key: "computacion",  label: "Computación",   emoji: "💻", vida: 72 },
  { key: "muebles",      label: "Muebles",       emoji: "🪑", vida: 84 },
  { key: "inmuebles",    label: "Inmuebles",     emoji: "🏢", vida: 960 },
  { key: "otros",        label: "Otros",         emoji: "📦", vida: 72 },
];
const cat = (key) => CATEGORIAS.find((c) => c.key === key) || CATEGORIAS[6];

const ESTADO_STYLE = {
  activo:       { label: "Activo",       bg: "#0d2a15", color: "#2ecc71", border: "#0d4020" },
  dado_de_baja: { label: "Dado de baja", bg: "#2d1515", color: "#e74c3c", border: "#4a2020" },
  vendido:      { label: "Vendido",      bg: "#2d2000", color: "#f39c12", border: "#4a3500" },
};

const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 6,
    padding: "8px 10px", fontSize: 12, color: "#ddd", boxSizing: "border-box",
    fontFamily: "Arial,sans-serif", width: "100%",
  },
  btnRojo: (disabled) => ({
    background: disabled ? "#333" : "#e30613", color: "#fff", border: "none",
    borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.7 : 1, minHeight: 38,
  }),
  btnGris: {
    background: "transparent", color: "#cbd5e1", border: "1px solid #444",
    borderRadius: 8, padding: "8px 16px", fontSize: 12, cursor: "pointer", minHeight: 38,
  },
};

const FORM_VACIO = {
  nombre: "", categoria: "maquinaria", fechaAdquisicion: "",
  valorAdquisicion: "", vidaUtilMeses: String(cat("maquinaria").vida),
  valorResidual: "1", depreciacionAcumuladaInicial: "0",
  facturaFolio: "", facturaRut: "", notas: "",
};

const ActivosFijos = () => {
  // Pulido desktop 27-ago: tarjetas más anchas (3-4 columnas reales) y modal
  // levemente más amplio; móvil intacto.
  const isMobile = useIsMobile();
  const [activos, setActivos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Filtro por estado (28-ago): chips con contador + badge cliqueable
  const [filtro, setFiltro] = useState("todos"); // todos | activo | vendido | dado_de_baja
  const [depPeriodo, setDepPeriodo] = useState(() => new Date().toISOString().slice(0, 7));
  const [depreciacion, setDepreciacion] = useState(null);

  // form = null (cerrado) | { _id?: string, ...campos } (nuevo si no hay _id)
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  // baja = null | { activo, tipo, fecha, motivo }
  const [baja, setBaja] = useState(null);
  const [bajando, setBajando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await backend.get("activo-fijo");
      setActivos(r.data || []);
      setError(null);
    } catch (e) { setError(e?.response?.data?.error || e.message); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    setDepreciacion(null);
    backend.get("activo-fijo/depreciacion", { params: { periodo: depPeriodo } }).then(r => setDepreciacion(r.data)).catch(() => setDepreciacion({ items: [], total: 0 }));
  }, [depPeriodo]);

  const abrirNuevo = () => { setFormError(null); setForm({ ...FORM_VACIO }); };

  const abrirEditar = (a) => {
    setFormError(null);
    setForm({
      _id: a._id,
      nombre: a.nombre || "",
      categoria: a.categoria || "otros",
      fechaAdquisicion: a.fechaAdquisicion ? String(a.fechaAdquisicion).slice(0, 10) : "",
      valorAdquisicion: String(a.valorAdquisicion ?? ""),
      vidaUtilMeses: String(a.vidaUtilMeses ?? ""),
      valorResidual: String(a.valorResidual ?? "1"),
      depreciacionAcumuladaInicial: String(a.depreciacionAcumuladaInicial ?? "0"),
      facturaFolio: a.facturaRef?.folio || "",
      facturaRut: a.facturaRef?.rutProveedor || "",
      notas: a.notas || "",
    });
  };

  // Al cambiar la categoría se pre-llena la vida útil sugerida SII (editable).
  const cambiarCategoria = (key) =>
    setForm((f) => ({ ...f, categoria: key, vidaUtilMeses: String(cat(key).vida) }));

  const guardar = async () => {
    if (!form || saving) return;
    if (!form.nombre.trim()) { setFormError("El nombre es obligatorio"); return; }
    if (!form.fechaAdquisicion) { setFormError("La fecha de adquisición es obligatoria"); return; }
    if (!(Number(form.valorAdquisicion) > 0)) { setFormError("El valor de adquisición debe ser mayor que 0"); return; }
    if (!(Number(form.vidaUtilMeses) >= 1)) { setFormError("La vida útil debe ser al menos 1 mes"); return; }
    setSaving(true);
    setFormError(null);
    const body = {
      nombre: form.nombre.trim(),
      categoria: form.categoria,
      fechaAdquisicion: form.fechaAdquisicion,
      valorAdquisicion: Math.round(Number(form.valorAdquisicion)),
      vidaUtilMeses: Math.round(Number(form.vidaUtilMeses)),
      valorResidual: Math.round(Number(form.valorResidual) || 0),
      depreciacionAcumuladaInicial: Math.round(Number(form.depreciacionAcumuladaInicial) || 0),
      facturaRef: { folio: form.facturaFolio.trim(), rutProveedor: form.facturaRut.trim() },
      notas: form.notas,
    };
    try {
      if (form._id) await backend.patch(`activo-fijo/${form._id}`, body);
      else await backend.post("activo-fijo", body);
      setForm(null);
      await cargar();
    } catch (e) { setFormError(e?.response?.data?.error || e.message); }
    setSaving(false);
  };

  const confirmarBaja = async () => {
    if (!baja || bajando) return;
    setBajando(true);
    try {
      await backend.patch(`activo-fijo/${baja.activo._id}/baja`, {
        tipo: baja.tipo, fecha: baja.fecha, motivo: baja.motivo,
      });
      setBaja(null);
      await cargar();
    } catch (e) { alert(e?.response?.data?.error || e.message); }
    setBajando(false);
  };

  const vigentes = activos.filter((a) => a.estado === "activo");
  const totalLibro = vigentes.reduce((s, a) => s + (a.valorLibro || 0), 0);
  const totalAdq   = vigentes.reduce((s, a) => s + (a.valorAdquisicion || 0), 0);
  const totalAcum  = vigentes.reduce((s, a) => s + (a.depreciacionAcumulada || 0), 0);

  const nPorEstado = activos.reduce((acc, a) => { acc[a.estado] = (acc[a.estado] || 0) + 1; return acc; }, {});
  const visibles = filtro === "todos" ? activos : activos.filter((a) => a.estado === filtro);
  const CHIPS = [
    ["todos", `Todos (${activos.length})`],
    ["activo", `Activos (${nPorEstado.activo || 0})`],
    ["vendido", `Vendidos (${nPorEstado.vendido || 0})`],
    ["dado_de_baja", `Baja (${nPorEstado.dado_de_baja || 0})`],
  ];

  return (
    <div>
      {/* ── Total del parque + alta ── */}
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "stretch" }}>
        {[
          { label: "Valor libro del parque", value: fmtM(totalLibro), color: "#2ecc71", sub: `${vigentes.length} activos vigentes` },
          { label: "Valor de adquisición",   value: fmtM(totalAdq),   color: "#3498db", sub: "suma histórica (vigentes)" },
          { label: "Depreciación acumulada", value: fmtM(totalAcum),  color: "#f39c12", sub: "a la fecha (vigentes)" },
        ].map((m) => (
          <div key={m.label} style={{ ...S.card, flex: "1 1 180px" }}>
            <div style={{ ...S.label, marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: m.color, ...num }}>{m.value}</div>
            <div style={{ fontSize: 10, color: "#999", marginTop: 2 }}>{m.sub}</div>
          </div>
        ))}
        <button onClick={abrirNuevo} style={{ ...S.btnRojo(false), alignSelf: "center", flexShrink: 0 }}>
          + Nuevo activo
        </button>
      </div>

      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <div><div style={S.label}>Tabla de depreciación mensual</div><strong>{depreciacion ? fmtM(depreciacion.total) : "Cargando…"}</strong></div>
          <input type="month" value={depPeriodo} onChange={e => setDepPeriodo(e.target.value)} style={{ ...S.input, width: 150 }} />
        </div>
        {depreciacion && (depreciacion.items.length ? <div style={{ overflowX: "auto" }}><table className="bini-table" style={{ width: "100%" }}><thead><tr><th>Activo</th><th>Categoría</th><th style={{ textAlign: "right" }}>Cuota del mes</th></tr></thead><tbody>{depreciacion.items.map(i => <tr key={i._id}><td>{i.nombre}</td><td>{cat(i.categoria).label}</td><td style={{ textAlign: "right", ...num }}>{fmtM(i.cuotaDelMes)}</td></tr>)}</tbody></table></div> : <div style={{ color: "var(--bini-text-muted)", fontSize: 12 }}>No hay cuotas de depreciación para este período.</div>)}
      </div>

      {error && (
        <div style={{ background: "#2d1515", border: "1px solid #e74c3c", borderRadius: 8, padding: "8px 12px", fontSize: 12, color: "#e74c3c", marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* ── Chips de estado con contador (28-ago) ── */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {CHIPS.map(([k, l]) => (
          <button key={k} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setFiltro(k)}
            style={{
              background: filtro === k ? "#3a1114" : "#1e1e1e",
              color: filtro === k ? "#ff8a8f" : "#999",
              border: `1px solid ${filtro === k ? "#e30613" : "#333"}`,
              borderRadius: 6, padding: "5px 12px", fontSize: 11, cursor: "pointer",
              whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums",
            }}>{l}</button>
        ))}
      </div>

      {/* ── Tarjetas por activo ── */}
      {loading ? (
        <div style={{ textAlign: "center", color: "#8a8a8a", fontSize: 12, padding: "28px 0" }}>Cargando…</div>
      ) : visibles.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12, padding: "28px 0" }}>
          {activos.length === 0
            ? 'Sin activos registrados. Parta con "+ Nuevo activo" (el torno, la camioneta…).'
            : "Nada con ese filtro."}
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${isMobile ? 270 : 330}px, 1fr))`, gap: isMobile ? 10 : 12 }}>
          {visibles.map((a) => {
            const c = cat(a.categoria);
            const es = ESTADO_STYLE[a.estado] || ESTADO_STYLE.activo;
            const base = a.baseDepreciable || 0;
            const pct = base > 0 ? Math.min(100, Math.round((a.depreciacionAcumulada / base) * 100)) : 0;
            const vigente = a.estado === "activo";
            return (
              <div key={a._id} style={{ ...S.card, opacity: vigente ? 1 : 0.6 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <span style={{ fontSize: 20, flexShrink: 0 }}>{c.emoji}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {a.nombre}
                    </div>
                    <div style={{ fontSize: 10, color: "#999" }}>{c.label} · adquirido {fmtD(a.fechaAdquisicion)}</div>
                  </div>
                  <span
                    onClick={() => setFiltro(filtro === a.estado ? "todos" : a.estado)}
                    title={filtro === a.estado ? "Quitar filtro" : `Ver solo "${es.label}"`}
                    className="t-02"
                    style={{
                      fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20, whiteSpace: "nowrap",
                      background: es.bg, color: es.color, border: `1px solid ${es.border}`, flexShrink: 0,
                      cursor: "pointer",
                    }}>{es.label}</span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 10 }}>
                  <div>
                    <div style={S.label}>Adquisición</div>
                    <div style={{ fontSize: 13, color: "#ddd", ...num }}>{fmtM(a.valorAdquisicion)}</div>
                  </div>
                  <div>
                    <div style={S.label}>Valor libro</div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#2ecc71", ...num }}>{fmtM(a.valorLibro)}</div>
                  </div>
                  <div>
                    <div style={S.label}>Dep. acumulada</div>
                    <div style={{ fontSize: 12, color: "#f39c12", ...num }}>{fmtM(a.depreciacionAcumulada)}</div>
                  </div>
                  <div>
                    <div style={S.label}>Cuota mensual</div>
                    <div style={{ fontSize: 12, color: "#bbb", ...num }}>{fmtM(a.cuotaMensual)}</div>
                  </div>
                </div>

                {/* Barra de avance de la depreciación */}
                <div style={{ background: "#161616", border: "1px solid #2e2e2e", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 4 }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: pct >= 100 ? "#555" : "#e30613", transition: "width .3s" }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "#8a8a8a", marginBottom: 10, ...num }}>
                  <span>{pct}% depreciado</span>
                  <span>
                    {vigente
                      ? (a.mesesRestantes > 0 ? `${a.mesesRestantes} meses restantes` : "totalmente depreciado")
                      : `${a.estado === "vendido" ? "vendido" : "baja"} el ${fmtD(a.fechaBaja)}`}
                  </span>
                </div>

                {vigente && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => abrirEditar(a)}
                      style={{ ...S.btnGris, flex: 1, minHeight: 38, padding: "5px 10px", fontSize: 11 }}>
                      Editar
                    </button>
                    <button onClick={() => setBaja({ activo: a, tipo: "baja", fecha: new Date().toISOString().slice(0, 10), motivo: "" })}
                      style={{ background: "#2d1515", color: "#e74c3c", border: "1px solid #4a2020", borderRadius: 8, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", flexShrink: 0, minHeight: 38 }}>
                      Dar de baja
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Modal nuevo/editar ── */}
      {form && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
          onClick={() => !saving && setForm(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: isMobile ? "min(480px, 94vw)" : "min(580px, 94vw)", maxHeight: "90vh", overflowY: "auto", background: "#1e1e1e", border: "1px solid #2e2e2e", borderRadius: 12, padding: isMobile ? 18 : 22 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#fff" }}>
                {form._id ? "Editar activo" : "Nuevo activo fijo"}
              </div>
              <button onClick={() => setForm(null)} style={{ background: "none", border: "none", color: "#999", fontSize: 20, cursor: "pointer" }}>×</button>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ ...S.label, marginBottom: 4 }}>Nombre</div>
              <input value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: Torno paralelo Pinacho 2m" style={S.input} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Categoría</div>
                <select value={form.categoria} onChange={(e) => cambiarCategoria(e.target.value)} style={S.input}>
                  {CATEGORIAS.map((c) => <option key={c.key} value={c.key}>{c.emoji} {c.label}</option>)}
                </select>
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Fecha de adquisición</div>
                <input type="date" value={form.fechaAdquisicion}
                  onChange={(e) => setForm((f) => ({ ...f, fechaAdquisicion: e.target.value }))} style={S.input} />
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Valor adquisición ($ neto)</div>
                <input type="number" min={0} value={form.valorAdquisicion}
                  onChange={(e) => setForm((f) => ({ ...f, valorAdquisicion: e.target.value }))} style={S.input} />
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Vida útil (meses)</div>
                <input type="number" min={1} value={form.vidaUtilMeses}
                  onChange={(e) => setForm((f) => ({ ...f, vidaUtilMeses: e.target.value }))} style={S.input} />
                <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>
                  Sugerencia SII {cat(form.categoria).label.toLowerCase()}: {cat(form.categoria).vida} meses
                </div>
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Valor residual ($)</div>
                <input type="number" min={0} value={form.valorResidual}
                  onChange={(e) => setForm((f) => ({ ...f, valorResidual: e.target.value }))} style={S.input} />
                <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>$1 simbólico es lo usual</div>
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Dep. acumulada inicial ($)</div>
                <input type="number" min={0} value={form.depreciacionAcumuladaInicial}
                  onChange={(e) => setForm((f) => ({ ...f, depreciacionAcumuladaInicial: e.target.value }))} style={S.input} />
                <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 2 }}>Solo activos con arrastre del contador</div>
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Factura (folio)</div>
                <input value={form.facturaFolio} onChange={(e) => setForm((f) => ({ ...f, facturaFolio: e.target.value }))}
                  placeholder="opcional" style={S.input} />
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>RUT proveedor</div>
                <input value={form.facturaRut} onChange={(e) => setForm((f) => ({ ...f, facturaRut: e.target.value }))}
                  placeholder="opcional" style={S.input} />
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <div style={{ ...S.label, marginBottom: 4 }}>Notas</div>
              <textarea rows={2} value={form.notas} onChange={(e) => setForm((f) => ({ ...f, notas: e.target.value }))}
                style={{ ...S.input, resize: "vertical" }} />
            </div>

            {formError && <div style={{ color: "#e74c3c", fontSize: 12, marginBottom: 10 }}>{formError}</div>}

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setForm(null)} disabled={saving} style={S.btnGris}>Cancelar</button>
              <button onClick={guardar} disabled={saving} style={S.btnRojo(saving)}>
                {saving ? "Guardando…" : form._id ? "Guardar cambios" : "Crear activo"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal dar de baja / vender ── */}
      {baja && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1400, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 12 }}
          onClick={() => !bajando && setBaja(null)}>
          <div onClick={(e) => e.stopPropagation()}
            style={{ width: "min(420px, 94vw)", background: "#1e1e1e", border: "1px solid #2e2e2e", borderRadius: 12, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 4 }}>
              Dar de baja · {baja.activo.nombre}
            </div>
            <div style={{ fontSize: 11, color: "#8a8a8a", marginBottom: 14 }}>
              Valor libro actual: {fmtM(baja.activo.valorLibro)}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Tipo</div>
                <select value={baja.tipo} onChange={(e) => setBaja((b) => ({ ...b, tipo: e.target.value }))} style={S.input}>
                  <option value="baja">Baja (desecho, obsoleto)</option>
                  <option value="venta">Venta</option>
                </select>
              </div>
              <div>
                <div style={{ ...S.label, marginBottom: 4 }}>Fecha</div>
                <input type="date" value={baja.fecha} onChange={(e) => setBaja((b) => ({ ...b, fecha: e.target.value }))} style={S.input} />
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ ...S.label, marginBottom: 4 }}>Motivo</div>
              <input value={baja.motivo} onChange={(e) => setBaja((b) => ({ ...b, motivo: e.target.value }))}
                placeholder="Ej: motor fundido, se vendió a…" style={S.input} />
            </div>

            <div style={{ background: "#2d1515", border: "1px solid #4a2020", borderRadius: 8, padding: "8px 12px", fontSize: 11, color: "#e74c3c", marginBottom: 12 }}>
              El activo deja de depreciarse y sale del parque vigente. El registro NO se borra: queda con su historia.
            </div>

            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setBaja(null)} disabled={bajando} style={S.btnGris}>Cancelar</button>
              <button onClick={confirmarBaja} disabled={bajando} style={S.btnRojo(bajando)}>
                {bajando ? "Procesando…" : baja.tipo === "venta" ? "Sí, marcar vendido" : "Sí, dar de baja"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivosFijos;
